import * as THREE from "three";
import { STLLoader, ThreeMFLoader } from "three-stdlib";

// Parse an STL/3MF file into normalized base geometries (centered on X/Y, min z = 0).
export function loadBaseFile(file) {
  return new Promise((resolve, reject) => {
    const ext = file.name.split(".").pop().toLowerCase();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = () => {
      try {
        let geoms = [];
        if (ext === "stl") {
          const g = new STLLoader().parse(reader.result);
          geoms = [g];
        } else if (ext === "3mf") {
          const obj = new ThreeMFLoader().parse(reader.result);
          obj.updateMatrixWorld(true);
          obj.traverse((c) => {
            if (c.isMesh && c.geometry) {
              geoms.push(c.geometry.clone().applyMatrix4(c.matrixWorld));
            }
          });
        } else {
          reject(new Error("Unsupported format. Use STL or 3MF."));
          return;
        }
        if (!geoms.length) {
          reject(new Error("No geometry found in file"));
          return;
        }
        const box = new THREE.Box3();
        geoms.forEach((g) => {
          g.computeBoundingBox();
          box.union(g.boundingBox);
        });
        const size = new THREE.Vector3();
        const center = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(center);
        const tx = -center.x;
        const ty = -center.y;
        const tz = -box.min.z;
        geoms.forEach((g) => {
          g.translate(tx, ty, tz);
          g.computeVertexNormals();
        });
        resolve({
          geometries: geoms,
          height: size.z,
          size: { x: size.x, y: size.y, z: size.z },
          name: file.name,
        });
      } catch (e) {
        reject(e);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

// Serialize base model geometries to plain JSON for project persistence.
export function serializeBase(base) {
  if (!base) return null;
  return {
    name: base.name,
    size: base.size,
    height: base.height,
    transform: base.transform || null,
    meshes: base.geometries.map((g) => ({
      positions: Array.from((g.index ? g.toNonIndexed() : g).attributes.position.array),
    })),
  };
}

export function deserializeBase(obj) {
  if (!obj || !obj.meshes) return null;
  const geometries = obj.meshes.map((m) => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(m.positions), 3));
    g.computeVertexNormals();
    return g;
  });
  return { geometries, size: obj.size, height: obj.height, name: obj.name, transform: obj.transform || undefined };
}
